"use client";

import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { CopyAnimatedButton } from "@/components/ui/CopyAnimatedButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IS_GATEWAY_V2 } from "@/contract/gateway-version";
import { PARENT_MESSAGE_KEY } from "@/hooks/checkout/useParentFieldValidation";
import type { PlacementMode } from "@/hooks/checkout/usePlacementResolution";
import { usePlacementResolution } from "@/hooks/checkout/usePlacementResolution";
import { REFERRER_MESSAGE_KEY } from "@/hooks/checkout/useReferrerFieldValidation";
import { $api } from "@/lib/api-client";
import { AddressField } from "./AddressField";
import { PlacementWarningNotice } from "./PlacementWarningNotice";

export interface ReferralResolution {
  canPurchase: boolean;
  referrerAddress: string | undefined;
  parentAddress: string | undefined;
  mode: PlacementMode;
  /** True when this purchase permanently fixes the buyer's placement. */
  willLockPlacement: boolean;
  /** True when the buyer has left the referrer field blank on their first purchase. */
  showNoReferrerWarning: boolean;
  blockedMessageKey: string | undefined;
}

interface ReferralSectionProps {
  /** RHF field name holding the referrer address (V2 "new" mode only). */
  referrerFieldName: string;
  /** RHF field name holding the parent address. */
  parentFieldName: string;
  /** Current cart total, in whole BV units — drives the seat-lock warning. */
  cartGrossBv: number;
  /**
   * The smallest per-unit BV among the cart's line items, in whole BV units.
   * Drives the below-minimum-purchase gate, which the gateway enforces PER
   * ORDER ITEM rather than on the cart total — defaults to `cartGrossBv` when
   * omitted, which is only correct for single-unit purchases (a cart total
   * that already multiplies quantity would let a below-minimum item hide
   * behind a large enough cart, then revert on submit).
   */
  minItemGrossBv?: number;
  /** Reports the resolved placement + purchase gate up to the page. */
  onResolutionChange: (resolution: ReferralResolution) => void;
  /**
   * Set to false when the page has its own place to show the no-referrer
   * warning (e.g. inside a terms-acceptance box) and reads
   * `showNoReferrerWarning` off the resolution callback instead. Defaults to
   * true so callers with no such box keep the warning next to the field.
   */
  renderNoReferrerWarningInline?: boolean;
}

/**
 * Owns placement resolution for a purchase form: two independent fields,
 * referrer (who invited the buyer) and parent (where they sit in the binary
 * tree), replacing the old single-field + "I have a referrer" checkbox.
 *
 * There is no client-side "guest mode" toggle any more. Leaving the referrer
 * blank on a first purchase IS the permanent-guest election — enforced by the
 * contract, not a UI branch — so it is surfaced as a disclosure notice next
 * to the (now-optional) field rather than a separate mode to opt into.
 *
 * Everything renders from {@link usePlacementResolution}, which reads the
 * CONNECTED wallet's live on-chain state on every render. Nothing here can
 * carry a stale or previously-typed address forward silently: a blank field
 * is always submitted as the zero address, never defaulted client-side to
 * something the buyer typed earlier.
 */
export const ReferralSection = ({
  referrerFieldName,
  parentFieldName,
  cartGrossBv,
  minItemGrossBv,
  onResolutionChange,
  renderNoReferrerWarningInline = true,
}: ReferralSectionProps) => {
  const t = useTranslations("checkout");
  const { watch } = useFormContext();

  const referrerTyped = watch(referrerFieldName);
  const parentTyped = watch(parentFieldName);

  const {
    mode,
    showReferrerField,
    showParentField,
    referrerStatus,
    lockedReferrerAddress,
    parentStatus,
    defaultParentFull,
    referrerAddress,
    parentAddress,
    willLockPlacement,
    canPurchase,
    blockedMessageKey,
    retry,
  } = usePlacementResolution(
    referrerTyped,
    parentTyped,
    cartGrossBv,
    minItemGrossBv,
  );

  const registeredParent = $api.useQuery(
    "get",
    "/user/me/parent",
    {},
    { enabled: mode === "placed" },
  );
  const registeredParentAddress =
    registeredParent.data?.data?.wallet_address ?? "";

  // Computed here (not just below the early returns) so the effect can
  // report it up on every render, including the ones that return early.
  const referrerBlank = (referrerTyped ?? "").trim().length === 0;
  const showNoReferrerWarning = showReferrerField && referrerBlank;

  // Keep the callback in a ref so the effect below depends only on primitives.
  const onChangeRef = useRef(onResolutionChange);
  useEffect(() => {
    onChangeRef.current = onResolutionChange;
  }, [onResolutionChange]);

  useEffect(() => {
    onChangeRef.current({
      canPurchase,
      referrerAddress,
      parentAddress,
      mode,
      willLockPlacement,
      showNoReferrerWarning,
      blockedMessageKey,
    });
  }, [
    canPurchase,
    referrerAddress,
    parentAddress,
    mode,
    willLockPlacement,
    showNoReferrerWarning,
    blockedMessageKey,
  ]);

  // Already placed needs no editable fields. Show the direct placement parent
  // so the buyer can verify their permanent tree position before paying.
  if (mode === "placed") {
    return (
      <div className="space-y-2 w-full">
        <Label htmlFor="checkout-registered-parent">
          {t("referrer.registeredParent")}
        </Label>
        <Input
          id="checkout-registered-parent"
          type="text"
          dir="ltr"
          value={registeredParentAddress}
          readOnly
          aria-readonly
          className="cursor-default bg-bg-secondary"
          endAdornment={
            registeredParentAddress ? (
              <CopyAnimatedButton
                text={registeredParentAddress}
                size={16}
                className="size-6"
              />
            ) : undefined
          }
        />
      </div>
    );
  }

  // A permanent guest has nothing to show or collect on later purchases --
  // the decision was already made and disclosed on their first one.
  if (mode === "permanent-guest") {
    return null;
  }

  if (mode === "no-wallet") {
    return (
      <p className="text-xs leading-tight text-text-secondary ms-1.5">
        {t("referrer.connectWallet")}
      </p>
    );
  }

  if (mode === "wallet-mismatch") {
    return (
      <p className="text-xs leading-tight text-semantic-error ms-1.5">
        {t("referrer.walletMismatch")}
      </p>
    );
  }

  if (mode === "loading") {
    return (
      <p className="text-xs leading-tight text-text-secondary ms-1.5">
        {t("referrer.verifying")}
      </p>
    );
  }

  // A transient RPC blip, not a permanent state -- the read is already
  // auto-retrying every 5s in the background (see usePlacementResolution),
  // this just stops the buyer staring at an unending spinner in the meantime
  // and gives them an immediate way to nudge it themselves.
  if (mode === "unreachable") {
    return (
      <div className="flex items-center justify-between gap-2 ms-1.5">
        <p className="text-xs leading-tight text-semantic-error">
          {t("referrer.accountUnreachable")}
        </p>
        <Button
          type="button"
          size="sm"
          variant="bordered"
          colors="secondary"
          onClick={() => retry()}
        >
          {t("referrer.retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {mode === "floating" ? (
        <div className="space-y-2 w-full">
          <Label htmlFor={`${parentFieldName}-locked-referrer`}>
            {t("referrer.lockedReferrer")}
          </Label>
          <Input
            id={`${parentFieldName}-locked-referrer`}
            type="text"
            dir="ltr"
            value={lockedReferrerAddress ?? ""}
            readOnly
            aria-readonly
            className="cursor-default bg-bg-secondary"
            endAdornment={
              lockedReferrerAddress ? (
                <CopyAnimatedButton
                  text={lockedReferrerAddress}
                  size={16}
                  className="size-6"
                />
              ) : undefined
            }
          />
        </div>
      ) : null}

      {showReferrerField ? (
        <AddressField
          name={referrerFieldName}
          label={t("referrer.label")}
          status={referrerStatus}
          messageKey={`checkout.referrer.${REFERRER_MESSAGE_KEY[referrerStatus]}`}
        />
      ) : null}

      {renderNoReferrerWarningInline && showNoReferrerWarning ? (
        <PlacementWarningNotice messageKey="checkout.referrer.permanentGuestWarning" />
      ) : null}

      {showParentField && (!showReferrerField || !referrerBlank) ? (
        <AddressField
          name={parentFieldName}
          label={
            IS_GATEWAY_V2 ? t("referrer.parentLabel") : t("referrer.label")
          }
          status={parentStatus}
          messageKey={`checkout.referrer.${PARENT_MESSAGE_KEY[parentStatus]}`}
        />
      ) : null}

      {defaultParentFull ? (
        <PlacementWarningNotice messageKey="checkout.referrer.defaultParentFull" />
      ) : null}

      {willLockPlacement && (!showReferrerField || !referrerBlank) ? (
        <PlacementWarningNotice messageKey="checkout.referrer.seatLockWarning" />
      ) : null}
    </div>
  );
};
